/**
 * หน้าต่างระบบร้านค้าตั้งแผงของผู้เล่น (Player Vending)
 *
 * 1. VendingSetupView: สำหรับเจ้าของร้าน ตั้งชื่อแผง เลือกของ ตั้งราคา และเปิด/ปิดร้าน
 * 2. VendingBrowseView: สำหรับผู้ซื้อ ดูรายการสินค้าในแผง เลือกจำนวน และซื้อ
 */

import {
  ITEMS,
  MARKET_TAX_RATE,
  VENDING_MAX_ITEMS,
  type ItemDef,
  type VendingItem,
  type VendingStall,
} from "@wickhaven/shared";
import type { GameWindow } from "./windows.js";

export interface VendingSetupCallbacks {
  onOpenStall: (title: string, items: VendingItem[]) => void;
  onCloseStall: () => void;
}

export interface VendingBrowseCallbacks {
  onBuyItem: (ownerId: string, itemId: string, count: number) => void;
  onClose: () => void;
}

export class VendingSetupView {
  private currentStall: VendingStall | null = null;
  private selectedItems: VendingItem[] = [];
  private stallTitle = "ร้านค้าสะดวกซื้อ";
  private note = "";

  constructor(
    private win: GameWindow,
    private callbacks: VendingSetupCallbacks,
  ) {}

  open(currentStall?: VendingStall, inventory?: Readonly<Record<string, number>>): void {
    this.currentStall = currentStall ?? null;
    if (!this.currentStall && this.selectedItems.length === 0) {
      this.selectedItems = [];
    }
    this.note = "";
    this.win.open();
    this.render(inventory);
  }

  setStall(stall: VendingStall | null, inventory?: Readonly<Record<string, number>>): void {
    this.currentStall = stall;
    if (!stall) this.selectedItems = [];
    this.render(inventory);
  }

  setNote(note: string, inventory?: Readonly<Record<string, number>>): void {
    this.note = note;
    this.render(inventory);
  }

  close(): void {
    this.win.close();
  }

  isOpen(): boolean {
    return this.win.isOpen();
  }

  render(inventory?: Readonly<Record<string, number>>): void {
    const body = this.win.body;
    body.innerHTML = "";

    const wrap = document.createElement("div");
    wrap.style.cssText = "display: flex; flex-direction: column; gap: 8px; width: 320px; font-size: 11px;";

    if (this.note) {
      const noteEl = document.createElement("div");
      noteEl.style.cssText = "padding: 4px 6px; background: rgba(255, 200, 50, 0.1); border: 1px solid rgba(255, 200, 50, 0.3); border-radius: 4px; color: #f5cf6d; font-size: 10px;";
      noteEl.textContent = this.note;
      wrap.appendChild(noteEl);
    }

    // ถ้าร้านเปิดอยู่แล้ว
    if (this.currentStall) {
      const activeBox = document.createElement("div");
      activeBox.style.cssText = "background: rgba(40, 90, 40, 0.2); border: 1px solid #34d399; border-radius: 6px; padding: 8px; display: flex; flex-direction: column; gap: 6px;";

      const header = document.createElement("div");
      header.style.cssText = "font-weight: bold; color: #6ee7b7; font-size: 12px;";
      header.textContent = `🟢 ร้านค้าของคุณกำลังเปิดอยู่: "${this.currentStall.title}"`;
      activeBox.appendChild(header);

      const desc = document.createElement("div");
      desc.style.cssText = "color: #aaa; font-size: 10px;";
      desc.textContent = "ร้านจะปิดอัตโนมัติหากคุณเดินออกจากจุดตั้งร้าน หรือสินค้าถูกซื้อจนหมด";
      activeBox.appendChild(desc);

      const itemsList = document.createElement("div");
      itemsList.style.cssText = "display: flex; flex-direction: column; gap: 3px; max-height: 140px; overflow-y: auto; background: rgba(0,0,0,0.3); padding: 4px; border-radius: 4px;";
      for (const it of this.currentStall.items) {
        const itemDef = ITEMS[it.itemId];
        const row = document.createElement("div");
        row.style.cssText = "display: flex; justify-content: space-between; padding: 2px 4px; background: rgba(255,255,255,0.04); border-radius: 3px;";
        row.innerHTML = `<span>${itemDef?.icon ?? "📦"} ${itemDef?.name ?? it.itemId} ×${it.count}</span><span style="color:#ffd700;">${it.price.toLocaleString()} G/ชิ้น</span>`;
        itemsList.appendChild(row);
      }
      activeBox.appendChild(itemsList);

      const closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.textContent = "ปิดร้านค้า";
      closeBtn.style.cssText = "padding: 4px 10px; background: #6b2424; border: 1px solid #a83b3b; border-radius: 4px; color: #fff; cursor: pointer; align-self: flex-end; font-size: 10px;";
      closeBtn.onclick = () => this.callbacks.onCloseStall();
      activeBox.appendChild(closeBtn);

      wrap.appendChild(activeBox);
      body.appendChild(wrap);
      return;
    }

    // ฟอร์มตั้งร้านใหม่
    const formBox = document.createElement("div");
    formBox.style.cssText = "display: flex; flex-direction: column; gap: 8px;";

    // ชื่อร้าน
    const titleRow = document.createElement("div");
    titleRow.style.cssText = "display: flex; flex-direction: column; gap: 2px;";
    const titleLabel = document.createElement("label");
    titleLabel.style.cssText = "font-weight: bold; color: #ddd; font-size: 10px;";
    titleLabel.textContent = "ชื่อร้านค้า (1-30 ตัวอักษร):";
    titleRow.appendChild(titleLabel);

    const titleInput = document.createElement("input");
    titleInput.type = "text";
    titleInput.maxLength = 30;
    titleInput.value = this.stallTitle;
    titleInput.style.cssText = "background: #1a2233; color: #fff; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; padding: 4px 6px; font-size: 11px;";
    titleInput.oninput = () => {
      this.stallTitle = titleInput.value;
    };
    titleRow.appendChild(titleInput);
    formBox.appendChild(titleRow);

    // รายการสินค้าที่เลือกไว้
    const tableBox = document.createElement("div");
    tableBox.style.cssText = "background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 6px; display: flex; flex-direction: column; gap: 4px;";

    const tableHeader = document.createElement("div");
    tableHeader.style.cssText = "display: flex; justify-content: space-between; font-weight: bold; color: #f0e6d2; font-size: 10px;";
    tableHeader.innerHTML = `<span>สินค้าที่วางขาย (${this.selectedItems.length}/${VENDING_MAX_ITEMS})</span><span>ราคาขาย/ชิ้น</span>`;
    tableBox.appendChild(tableHeader);

    const listContainer = document.createElement("div");
    listContainer.style.cssText = "display: flex; flex-direction: column; gap: 3px; min-height: 60px; max-height: 120px; overflow-y: auto;";

    if (this.selectedItems.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = "color: #777; font-style: italic; text-align: center; margin-top: 18px; font-size: 10px;";
      empty.textContent = "(ยังไม่ได้เลือกสินค้าที่ต้องการวางขาย)";
      listContainer.appendChild(empty);
    } else {
      this.selectedItems.forEach((it, idx) => {
        const itemDef = ITEMS[it.itemId];
        const row = document.createElement("div");
        row.style.cssText = "display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.04); padding: 2px 4px; border-radius: 3px;";

        const left = document.createElement("div");
        left.style.cssText = "display: flex; align-items: center; gap: 4px;";
        const rm = document.createElement("button");
        rm.type = "button";
        rm.textContent = "×";
        rm.title = "ลบออก";
        rm.style.cssText = "background: none; border: none; color: #ff6b6b; cursor: pointer; font-size: 11px; padding: 0 3px;";
        rm.onclick = () => {
          this.selectedItems.splice(idx, 1);
          this.render(inventory);
        };
        left.appendChild(rm);

        const nameSpan = document.createElement("span");
        nameSpan.textContent = `${itemDef?.icon ?? "📦"} ${itemDef?.name ?? it.itemId} ×${it.count}`;
        left.appendChild(nameSpan);
        row.appendChild(left);

        const priceInput = document.createElement("input");
        priceInput.type = "number";
        priceInput.min = "1";
        priceInput.value = String(it.price);
        priceInput.style.cssText = "width: 70px; text-align: right; background: #1a2233; color: #ffd700; border: 1px solid rgba(255,255,255,0.2); border-radius: 3px; padding: 1px 3px; font-size: 10px;";
        priceInput.onchange = () => {
          it.price = Math.max(1, parseInt(priceInput.value, 10) || 1);
        };
        row.appendChild(priceInput);

        listContainer.appendChild(row);
      });
    }
    tableBox.appendChild(listContainer);
    formBox.appendChild(tableBox);

    // ส่วนเพิ่มสินค้าจากกระเป๋า
    if (this.selectedItems.length < VENDING_MAX_ITEMS && inventory) {
      const addWrap = document.createElement("div");
      addWrap.style.cssText = "display: flex; gap: 4px; align-items: center; background: rgba(255,255,255,0.03); padding: 4px; border-radius: 4px;";

      const sel = document.createElement("select");
      sel.style.cssText = "flex: 1; background: #1a2233; color: #fff; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; padding: 2px 4px; font-size: 10px;";
      const defOpt = document.createElement("option");
      defOpt.value = "";
      defOpt.textContent = "-- เลือกไอเทมในกระเป๋า --";
      sel.appendChild(defOpt);

      for (const [id, count] of Object.entries(inventory)) {
        if (count > 0 && !this.selectedItems.some((i) => i.itemId === id)) {
          const itemDef = ITEMS[id];
          const opt = document.createElement("option");
          opt.value = id;
          opt.textContent = `${itemDef?.icon ?? "📦"} ${itemDef?.name ?? id} (${count})`;
          sel.appendChild(opt);
        }
      }

      const countIn = document.createElement("input");
      countIn.type = "number";
      countIn.min = "1";
      countIn.value = "1";
      countIn.title = "จำนวน";
      countIn.style.cssText = "width: 40px; background: #1a2233; color: #fff; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; padding: 2px 3px; font-size: 10px;";

      const priceIn = document.createElement("input");
      priceIn.type = "number";
      priceIn.min = "1";
      priceIn.value = "100";
      priceIn.title = "ราคาต่อชิ้น";
      priceIn.style.cssText = "width: 55px; background: #1a2233; color: #ffd700; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; padding: 2px 3px; font-size: 10px;";

      const addBtn = document.createElement("button");
      addBtn.type = "button";
      addBtn.textContent = "+ เพิ่ม";
      addBtn.style.cssText = "padding: 2px 8px; background: #2f5485; border: 1px solid #4872ab; border-radius: 4px; color: #fff; cursor: pointer; font-size: 10px;";
      addBtn.onclick = () => {
        if (sel.value) {
          const have = inventory[sel.value] ?? 0;
          const c = Math.min(have, Math.max(1, parseInt(countIn.value, 10) || 1));
          const p = Math.max(1, parseInt(priceIn.value, 10) || 1);
          this.selectedItems.push({ itemId: sel.value, count: c, price: p });
          this.render(inventory);
        }
      };

      addWrap.appendChild(sel);
      addWrap.appendChild(countIn);
      addWrap.appendChild(priceIn);
      addWrap.appendChild(addBtn);
      formBox.appendChild(addWrap);
    }

    // หมายเหตุภาษีตลาด
    const taxNote = document.createElement("div");
    taxNote.style.cssText = "font-size: 9.5px; color: #aaa;";
    taxNote.textContent = `* ภาษีตลาด ${MARKET_TAX_RATE * 100}% จะถูกหักจากรายได้เมื่อสินค้าถูกขาย`;
    formBox.appendChild(taxNote);

    // ปุ่มเปิดร้าน
    const openBtn = document.createElement("button");
    openBtn.type = "button";
    openBtn.textContent = "🏪 เปิดร้านค้า";
    openBtn.style.cssText = "padding: 5px 12px; background: #2b7a3e; border: 1px solid #4ac269; border-radius: 4px; color: #fff; cursor: pointer; font-size: 11px; font-weight: bold; align-self: flex-end;";
    openBtn.onclick = () => {
      this.stallTitle = titleInput.value.trim() || this.stallTitle;
      this.callbacks.onOpenStall(this.stallTitle, this.selectedItems);
    };
    formBox.appendChild(openBtn);

    wrap.appendChild(formBox);
    body.appendChild(wrap);
  }
}

export class VendingBrowseView {
  private stall: VendingStall | null = null;
  private note = "";

  constructor(
    private win: GameWindow,
    private callbacks: VendingBrowseCallbacks,
  ) {}

  showStall(stall: VendingStall): void {
    this.stall = stall;
    this.note = "";
    this.win.open();
    this.render();
  }

  updateStall(stall: VendingStall): void {
    this.stall = stall;
    this.render();
  }

  setNote(note: string): void {
    this.note = note;
    this.render();
  }

  close(): void {
    this.stall = null;
    this.note = "";
    this.win.close();
  }

  isOpen(): boolean {
    return this.win.isOpen();
  }

  render(myGold = 0): void {
    const body = this.win.body;
    body.innerHTML = "";

    if (!this.stall) {
      body.textContent = "ไม่มีข้อมูลร้านค้า";
      return;
    }

    const wrap = document.createElement("div");
    wrap.style.cssText = "display: flex; flex-direction: column; gap: 8px; width: 300px; font-size: 11px;";

    // หัวร้าน
    const header = document.createElement("div");
    header.style.cssText = "display: flex; flex-direction: column; gap: 2px; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 4px;";
    const titleEl = document.createElement("div");
    titleEl.style.cssText = "font-weight: bold; color: #ffd700; font-size: 12px;";
    titleEl.textContent = `🏪 ${this.stall.title}`;
    header.appendChild(titleEl);

    const ownerEl = document.createElement("div");
    ownerEl.style.cssText = "color: #aaa; font-size: 10px;";
    ownerEl.textContent = `เจ้าของร้าน: ${this.stall.ownerName}`;
    header.appendChild(ownerEl);
    wrap.appendChild(header);

    if (this.note) {
      const noteEl = document.createElement("div");
      noteEl.style.cssText = "padding: 4px 6px; background: rgba(255, 200, 50, 0.1); border: 1px solid rgba(255, 200, 50, 0.3); border-radius: 4px; color: #f5cf6d; font-size: 10px;";
      noteEl.textContent = this.note;
      wrap.appendChild(noteEl);
    }

    // รายการสินค้า
    const itemsList = document.createElement("div");
    itemsList.style.cssText = "display: flex; flex-direction: column; gap: 4px; max-height: 200px; overflow-y: auto;";

    if (this.stall.items.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = "color: #777; font-style: italic; text-align: center; margin-top: 20px;";
      empty.textContent = "สินค้าในร้านหมดแล้ว";
      itemsList.appendChild(empty);
    } else {
      for (const it of this.stall.items) {
        const itemDef = ITEMS[it.itemId];
        const row = document.createElement("div");
        row.style.cssText = "display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.04); padding: 4px 6px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.06);";

        const left = document.createElement("div");
        left.style.cssText = "display: flex; flex-direction: column;";
        const nameSpan = document.createElement("span");
        nameSpan.style.cssText = "font-weight: bold; color: #f0e6d2;";
        nameSpan.textContent = `${itemDef?.icon ?? "📦"} ${itemDef?.name ?? it.itemId}`;
        left.appendChild(nameSpan);

        const stockSpan = document.createElement("span");
        stockSpan.style.cssText = "font-size: 9.5px; color: #888;";
        stockSpan.textContent = `เหลือ ${it.count} ชิ้น`;
        left.appendChild(stockSpan);
        row.appendChild(left);

        const right = document.createElement("div");
        right.style.cssText = "display: flex; align-items: center; gap: 4px;";

        const priceSpan = document.createElement("span");
        priceSpan.style.cssText = "color: #ffd700; font-weight: bold; margin-right: 4px;";
        priceSpan.textContent = `${it.price.toLocaleString()} G`;
        right.appendChild(priceSpan);

        const qtyInput = document.createElement("input");
        qtyInput.type = "number";
        qtyInput.min = "1";
        qtyInput.max = String(it.count);
        qtyInput.value = "1";
        qtyInput.style.cssText = "width: 40px; background: #1a2233; color: #fff; border: 1px solid rgba(255,255,255,0.2); border-radius: 3px; padding: 2px 3px; font-size: 10px;";
        right.appendChild(qtyInput);

        const buyBtn = document.createElement("button");
        buyBtn.type = "button";
        buyBtn.textContent = "ซื้อ";
        buyBtn.style.cssText = "padding: 3px 8px; background: #2f5485; border: 1px solid #4872ab; border-radius: 3px; color: #fff; cursor: pointer; font-size: 10px; font-weight: bold;";
        buyBtn.onclick = () => {
          const qty = Math.min(it.count, Math.max(1, parseInt(qtyInput.value, 10) || 1));
          this.callbacks.onBuyItem(this.stall!.ownerId, it.itemId, qty);
        };
        right.appendChild(buyBtn);

        row.appendChild(right);
        itemsList.appendChild(row);
      }
    }
    wrap.appendChild(itemsList);

    body.appendChild(wrap);
  }
}
